import React, { useEffect, useMemo, useState } from "react";
import { Button } from "@lightspeed/flame/Button";

const overlayStyle = {
  position: "fixed",
  inset: 0,
  background: "rgba(15, 23, 42, 0.45)",
  zIndex: 10000,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 24,
};

const modalStyle = {
  width: "100%",
  maxWidth: 760,
  background: "#fff",
  borderRadius: 8,
  boxShadow: "0 24px 80px rgba(0,0,0,0.25)",
  overflow: "hidden",
};

const selectStyle = {
  width: "100%",
  minHeight: 36,
  border: "1px solid #cbd5e1",
  borderRadius: 6,
  padding: "6px 8px",
  color: "#0f172a",
  background: "#fff",
};

const searchStyle = {
  width: "100%",
  minHeight: 34,
  border: "1px solid #cbd5e1",
  borderRadius: 6,
  padding: "6px 8px",
  color: "#0f172a",
  background: "#fff",
};

const splitClassDepartmentValue = (value, classes, departments) => {
  const rawValue = String(value || "").trim();
  if (!rawValue) return { className: "", departmentName: "" };

  for (const classRow of classes || []) {
    for (const departmentRow of departments || []) {
      const className = classRow.description;
      const departmentName = departmentRow.description;
      if (`${className}:${departmentName}` === rawValue) {
        return { className, departmentName };
      }
    }
  }

  const classMatch = (classes || []).find((row) => row.description === rawValue);
  if (classMatch) return { className: classMatch.description, departmentName: "" };

  const departmentMatch = (departments || []).find((row) => row.description === rawValue);
  if (departmentMatch) return { className: "", departmentName: departmentMatch.description };

  return { className: rawValue, departmentName: "" };
};

export const buildClassDepartmentValue = (className, departmentName) =>
  [className, departmentName].map((part) => String(part || "").trim()).filter(Boolean).join(":");

const filterRows = (rows, searchText) => {
  const term = String(searchText || "").trim().toLowerCase();
  if (!term) return rows || [];

  return (rows || []).filter((row) =>
    String(row.description || "").toLowerCase().includes(term)
  );
};

const QuickBooksClassDepartmentModal = ({
  open,
  value,
  classDepartments,
  onClose,
  onApply,
}) => {
  const classes = useMemo(() => classDepartments?.classes || [], [classDepartments]);
  const departments = useMemo(() => classDepartments?.departments || [], [classDepartments]);
  const [selectedClass, setSelectedClass] = useState("");
  const [selectedDepartment, setSelectedDepartment] = useState("");
  const [classSearch, setClassSearch] = useState("");
  const [departmentSearch, setDepartmentSearch] = useState("");

  useEffect(() => {
    if (!open) return;

    const selected = splitClassDepartmentValue(value, classes, departments);
    setSelectedClass(selected.className);
    setSelectedDepartment(selected.departmentName);
    setClassSearch("");
    setDepartmentSearch("");
  }, [open, value, classes, departments]);

  const filteredClasses = useMemo(
    () => filterRows(classes, classSearch),
    [classes, classSearch]
  );
  const filteredDepartments = useMemo(
    () => filterRows(departments, departmentSearch),
    [departments, departmentSearch]
  );

  if (!open) return null;

  const handleApply = () => {
    onApply(buildClassDepartmentValue(selectedClass, selectedDepartment));
    onClose();
  };

  return (
    <div onClick={onClose} style={overlayStyle}>
      <div onClick={(event) => event.stopPropagation()} style={modalStyle}>
        <div
          style={{
            padding: "16px 20px",
            borderBottom: "1px solid #e2e8f0",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ fontSize: 18, fontWeight: 700, color: "#0f172a" }}>
              Class / Department
            </div>
            <button
              type="button"
              onClick={() => {
                setSelectedClass("");
                setSelectedDepartment("");
              }}
              style={{
                border: "1px solid #dc2626",
                background: "#dc2626",
                color: "#fff",
                borderRadius: 6,
                cursor: "pointer",
                fontSize: 12,
                fontWeight: 700,
                padding: "4px 8px",
              }}
            >
              Clear
            </button>
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{
              border: "none",
              background: "transparent",
              fontSize: 20,
              cursor: "pointer",
              color: "#64748b",
            }}
          >
            &times;
          </button>
        </div>

        <div style={{ padding: 20 }}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
              gap: 16,
            }}
          >
            <label style={{ display: "grid", gap: 8, color: "#1e293b" }}>
              <span style={{ fontSize: 13, fontWeight: 700 }}>Class</span>
              <input
                type="text"
                value={classSearch}
                onChange={(event) => setClassSearch(event.target.value)}
                placeholder="Search class..."
                style={searchStyle}
              />
              <select
                value={selectedClass}
                onChange={(event) => setSelectedClass(event.target.value)}
                style={selectStyle}
              >
                <option value="">Select class</option>
                {selectedClass && !classes.some((row) => row.description === selectedClass) && (
                  <option value={selectedClass}>{selectedClass}</option>
                )}
                {filteredClasses.map((row) => (
                  <option key={`class-${row.id}-${row.description}`} value={row.description}>
                    {row.description}
                  </option>
                ))}
              </select>
            </label>

            <label style={{ display: "grid", gap: 8, color: "#1e293b" }}>
              <span style={{ fontSize: 13, fontWeight: 700 }}>Department</span>
              <input
                type="text"
                value={departmentSearch}
                onChange={(event) => setDepartmentSearch(event.target.value)}
                placeholder="Search department..."
                style={searchStyle}
              />
              <select
                value={selectedDepartment}
                onChange={(event) => setSelectedDepartment(event.target.value)}
                style={selectStyle}
              >
                <option value="">Select department</option>
                {selectedDepartment &&
                  !departments.some((row) => row.description === selectedDepartment) && (
                    <option value={selectedDepartment}>{selectedDepartment}</option>
                  )}
                {filteredDepartments.map((row) => (
                  <option key={`department-${row.id}-${row.description}`} value={row.description}>
                    {row.description}
                  </option>
                ))}
              </select>
            </label>
          </div>
        </div>

        <div
          style={{
            padding: "16px 20px",
            borderTop: "1px solid #e2e8f0",
            display: "flex",
            justifyContent: "flex-end",
            gap: 8,
          }}
        >
          <Button size="small" variant="secondary" onClick={onClose}>
            Close
          </Button>
          <Button size="small" fill="true" onClick={handleApply}>
            Apply
          </Button>
        </div>
      </div>
    </div>
  );
};

export default QuickBooksClassDepartmentModal;
